/* eslint-disable no-restricted-globals */
// ============================================================================
// 📦 VERSION: 2025.12.14.1850
// ============================================================================
// CHANGELOG:
// 2025.12.14.1850 - FIX: Picture sort arrows now work (handleMovePicture implemented)
// 2025.12.14.1815 - Removed duplicate confirm dialog (panel already confirms)
// 2025.12.14.1810 - FIX: Delete game now calls API (was only updating local state)
// 2025.12.14.1620 - Challenge handlers now just update local state (API called by panel)
// 2025.12.14.1145 - FIX: Delete/Edit/Toggle questions now call API (persist to database)
// 2025.12.14.1145 - NEW: Delete All Questions feature per quiz
// 2025.12.02.1730 - All challenges now use snake_case consistently
// 2025.12.02.1720 - Challenges now load from API instead of localStorage
// 2025.12.02.1610 - Fixed messages.map error with safety checks
// 2025.12.02.1545 - Added Global Adverts tab and Contact Messages tab
// 2025.12.02.1330 - Pictures stored in database via API (no more localStorage)
// 2025.12.01.0400 - Initial admin authentication wrapper
// ============================================================================
// TRAK Quiz Game - Admin Application
// Wraps CompleteGameAdminPanel with admin authentication and API
// Access via: /admin
// ============================================================================
import React, { useState, useEffect } from 'react';
import CompleteGameAdminPanel from './components/CompleteGameAdminPanel';

const API_URL = 'https://api.traktag.com/api';

// ============================================
// ADMIN LOGIN SCREEN
// ============================================
const AdminLogin = ({ onLogin }) => {
  const [email, setEmail] = useState('');
  const [password, setPassword] = useState('');
  const [error, setError] = useState('');
  const [loading, setLoading] = useState(false);

  const handleSubmit = async (e) => {
    e.preventDefault();
    setError('');
    setLoading(true);

    try {
      const res = await fetch(`${API_URL}/admin/login`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ email, password })
      });
      const data = await res.json();

      if (data.error) {
        setError(data.error);
      } else if (data.token) {
        localStorage.setItem('admin_token', data.token);
        onLogin(data.admin, data.token);
      }
    } catch (err) {
      setError('Connection failed');
    }
    setLoading(false);
  };

  return (
    <div className="min-h-screen bg-gray-900 flex items-center justify-center p-4">
      <div className="bg-gray-800 rounded-2xl p-8 w-full max-w-md">
        <div className="text-center mb-8">
          <h1 className="text-2xl font-black text-white">🔧 TRAK Admin</h1>
          <p className="text-gray-400 mt-2">Game Management Portal</p>
        </div>

        {error && (
          <div className="bg-red-600/20 border border-red-500 text-red-400 px-4 py-2 rounded-lg mb-4">
            {error}
          </div>
        )}

        <form onSubmit={handleSubmit}>
          <div className="mb-4">
            <label className="text-gray-400 text-sm block mb-1">Email</label>
            <input
              type="email"
              value={email}
              onChange={(e) => setEmail(e.target.value)}
              className="w-full bg-gray-700 text-white px-4 py-3 rounded-lg focus:outline-none focus:ring-2 focus:ring-purple-500"
              required
            />
          </div>
          <div className="mb-6">
            <label className="text-gray-400 text-sm block mb-1">Password</label>
            <input
              type="password"
              value={password}
              onChange={(e) => setPassword(e.target.value)}
              className="w-full bg-gray-700 text-white px-4 py-3 rounded-lg focus:outline-none focus:ring-2 focus:ring-purple-500"
              required
            />
          </div>
          <button
            type="submit"
            disabled={loading}
            className="w-full bg-purple-600 hover:bg-purple-500 text-white font-bold py-3 rounded-lg"
          >
            {loading ? 'Logging in...' : 'Login'}
          </button>
        </form>
      </div>
    </div>
  );
};

// ============================================
// GLOBAL ADVERTS PANEL
// ============================================
const GlobalAdvertsPanel = ({ settings, onSave }) => {
  const [formData, setFormData] = useState({
    fallback_mobile_banner: '',
    fallback_desktop_banner: '',
    fallback_static_ad: '',
    fallback_exit_ad: '',
    fallback_ad_click_url: ''
  });

  useEffect(() => {
    if (settings) {
      setFormData({
        fallback_mobile_banner: settings.fallback_mobile_banner || '',
        fallback_desktop_banner: settings.fallback_desktop_banner || '',
        fallback_static_ad: settings.fallback_static_ad || '',
        fallback_exit_ad: settings.fallback_exit_ad || '',
        fallback_ad_click_url: settings.fallback_ad_click_url || ''
      });
    }
  }, [settings]);

  const handleSave = () => {
    onSave(formData);
  };

  return (
    <div className="max-w-3xl mx-auto">
      <div className="bg-gray-800 rounded-xl p-6">
        <h2 className="text-xl font-bold text-white mb-2">📺 Global Fallback Adverts</h2>
        <p className="text-gray-400 text-sm mb-6">
          These ads show when a quiz doesn't have its own specific ads configured.
          Leave blank to show a placeholder.
        </p>

        <div className="space-y-4">
          {/* Mobile Banner */}
          <div>
            <label className="text-white text-sm block mb-1">📱 Mobile Banner (340×50)</label>
            <input
              type="url"
              value={formData.fallback_mobile_banner}
              onChange={(e) => setFormData({...formData, fallback_mobile_banner: e.target.value})}
              placeholder="https://your-ad-network.com/mobile-banner"
              className="w-full bg-gray-700 text-white px-4 py-2 rounded-lg"
            />
            {formData.fallback_mobile_banner && (
              <div className="mt-2 p-2 bg-gray-900 rounded">
                <img src={formData.fallback_mobile_banner} alt="Preview" className="h-12 object-contain" />
              </div>
            )}
          </div>

          {/* Desktop Banner */}
          <div>
            <label className="text-white text-sm block mb-1">🖥️ Desktop Banner (728×90)</label>
            <input
              type="url"
              value={formData.fallback_desktop_banner}
              onChange={(e) => setFormData({...formData, fallback_desktop_banner: e.target.value})}
              placeholder="https://your-ad-network.com/desktop-banner"
              className="w-full bg-gray-700 text-white px-4 py-2 rounded-lg"
            />
            {formData.fallback_desktop_banner && (
              <div className="mt-2 p-2 bg-gray-900 rounded">
                <img src={formData.fallback_desktop_banner} alt="Preview" className="h-20 object-contain" />
              </div>
            )}
          </div>

          {/* Static Ad */}
          <div>
            <label className="text-white text-sm block mb-1">📺 Static Ad (300×250)</label>
            <input
              type="url"
              value={formData.fallback_static_ad}
              onChange={(e) => setFormData({...formData, fallback_static_ad: e.target.value})}
              placeholder="https://your-ad-network.com/static-ad"
              className="w-full bg-gray-700 text-white px-4 py-2 rounded-lg"
            />
          </div>

          {/* Exit Ad */}
          <div>
            <label className="text-white text-sm block mb-1">🚪 Exit Ad (320×480)</label>
            <input
              type="url"
              value={formData.fallback_exit_ad}
              onChange={(e) => setFormData({...formData, fallback_exit_ad: e.target.value})}
              placeholder="https://your-ad-network.com/exit-ad"
              className="w-full bg-gray-700 text-white px-4 py-2 rounded-lg"
            />
          </div>

          {/* Click URL */}
          <div>
            <label className="text-white text-sm block mb-1">🔗 Default Click URL</label>
            <input
              type="url"
              value={formData.fallback_ad_click_url}
              onChange={(e) => setFormData({...formData, fallback_ad_click_url: e.target.value})}
              placeholder="https://traktag.com/advertise"
              className="w-full bg-gray-700 text-white px-4 py-2 rounded-lg"
            />
            <p className="text-gray-500 text-xs mt-1">Where users go when they click fallback ads</p>
          </div>
        </div>

        <button
          onClick={handleSave}
          className="mt-6 w-full bg-purple-600 hover:bg-purple-500 text-white py-3 rounded-lg font-bold"
        >
          💾 Save Global Ad Settings
        </button>

        {/* Info Box */}
        <div className="mt-6 bg-blue-900/30 border border-blue-600 rounded-lg p-4">
          <h3 className="text-blue-300 font-bold text-sm mb-2">💡 How Ad Priority Works</h3>
          <ol className="text-blue-200 text-xs space-y-1 list-decimal list-inside">
            <li>Quiz-specific ads are used first (set in Games → Edit)</li>
            <li>If no quiz ad, global fallback ads are used (set here)</li>
            <li>If no fallback, placeholder images are shown</li>
          </ol>
        </div>
      </div>
    </div>
  );
};

// ============================================
// CONTACT MESSAGES PANEL
// ============================================
const ContactMessagesPanel = ({ messages, onUpdateStatus, onDelete }) => {
  const [filter, setFilter] = useState('all');
  
  const filteredMessages = messages.filter(msg => {
    if (filter === 'all') return true;
    return msg.status === filter;
  });

  const getStatusColor = (status) => {
    switch(status) {
      case 'new': return 'bg-red-600';
      case 'read': return 'bg-yellow-600';
      case 'replied': return 'bg-green-600';
      default: return 'bg-gray-600';
    }
  };

  return (
    <div className="max-w-4xl mx-auto">
      <div className="bg-gray-800 rounded-xl p-6">
        <div className="flex justify-between items-center mb-6">
          <h2 className="text-xl font-bold text-white">📬 Contact Messages</h2>
          <select
            value={filter}
            onChange={(e) => setFilter(e.target.value)}
            className="bg-gray-700 text-white px-3 py-2 rounded-lg"
          >
            <option value="all">All Messages</option>
            <option value="new">New</option>
            <option value="read">Read</option>
            <option value="replied">Replied</option>
          </select>
        </div>

        {filteredMessages.length === 0 ? (
          <p className="text-gray-400 text-center py-8">No messages found</p>
        ) : (
          <div className="space-y-4">
            {filteredMessages.map(msg => (
              <div key={msg.contact_id} className="bg-gray-700 rounded-lg p-4">
                <div className="flex justify-between items-start mb-2">
                  <div>
                    <span className={`${getStatusColor(msg.status)} text-white text-xs px-2 py-1 rounded mr-2`}>
                      {msg.status?.toUpperCase()}
                    </span>
                    <span className="text-white font-bold">{msg.name}</span>
                    <span className="text-gray-400 text-sm ml-2">{msg.email}</span>
                  </div>
                  <span className="text-gray-500 text-xs">
                    {new Date(msg.created_at).toLocaleString()}
                  </span>
                </div>
                {msg.subject && (
                  <p className="text-purple-300 text-sm mb-1">Subject: {msg.subject}</p>
                )}
                <p className="text-gray-300 text-sm mb-3">{msg.message}</p>
                <div className="flex gap-2">
                  {msg.status === 'new' && (
                    <button
                      onClick={() => onUpdateStatus(msg.contact_id, 'read')}
                      className="bg-yellow-600 hover:bg-yellow-500 text-white text-xs px-3 py-1 rounded"
                    >
                      Mark Read
                    </button>
                  )}
                  {msg.status !== 'replied' && (
                    <button
                      onClick={() => onUpdateStatus(msg.contact_id, 'replied')}
                      className="bg-green-600 hover:bg-green-500 text-white text-xs px-3 py-1 rounded"
                    >
                      Mark Replied
                    </button>
                  )}
                  <a
                    href={`mailto:${msg.email}?subject=Re: ${msg.subject || 'Your TRAKtag message'}`}
                    className="bg-blue-600 hover:bg-blue-500 text-white text-xs px-3 py-1 rounded"
                  >
                    Reply via Email
                  </a>
                  <button
                    onClick={() => onDelete(msg.contact_id)}
                    className="bg-red-600 hover:bg-red-500 text-white text-xs px-3 py-1 rounded ml-auto"
                  >
                    Delete
                  </button>
                </div>
              </div>
            ))}
          </div>
        )}
      </div>
    </div>
  );
};

// ============================================
// MAIN ADMIN WRAPPER (Authenticated)
// ============================================
const AdminWrapper = ({ admin, token, onLogout }) => {
  const [games, setGames] = useState([]);
  const [questions, setQuestions] = useState([]);
  const [pictures, setPictures] = useState([]);
  const [challenges, setChallenges] = useState([]);
  const [messages, setMessages] = useState([]);
  const [rewards, setRewards] = useState([]);
  const [players, setPlayers] = useState([]);
  const [editingGame, setEditingGame] = useState(null);
  const [editGameData, setEditGameData] = useState(null);
  const [pictureGridSettings, setPictureGridSettings] = useState({
    enabled: true,
    showGrid: true
  });
  const [activeAdminTab, setActiveAdminTab] = useState('games');
  const [globalSettings, setGlobalSettings] = useState({});
  const [contactMessages, setContactMessages] = useState([]);
  const [contactCount, setContactCount] = useState(0);

  const authHeaders = {
    'Content-Type': 'application/json',
    'Authorization': `Bearer ${token}`
  };

  // Initial data load
  useEffect(() => {
    loadGames();
    loadPlayers();
    loadLocalData();
    loadGlobalSettings();
    loadContactCount();
  }, []);

  // Load contact count for badge
  const loadContactCount = async () => {
    try {
      const res = await fetch(`${API_URL}/admin/contact/count`, { headers: authHeaders });
      if (res.ok) {
        const data = await res.json();
        setContactCount(data.count || 0);
      }
    } catch (err) {
      console.error('Failed to load contact count:', err);
    }
  };

  // Load contact messages
  const loadContactMessages = async () => {
    try {
      const res = await fetch(`${API_URL}/admin/contact`, { headers: authHeaders });
      if (res.ok) {
        const data = await res.json();
        setContactMessages(Array.isArray(data) ? data : []);
      }
    } catch (err) {
      console.error('Failed to load contact messages:', err);
    }
  };

  // Load global settings
  const loadGlobalSettings = async () => {
    try {
      const res = await fetch(`${API_URL}/admin/settings`, { headers: authHeaders });
      if (res.ok) {
        const data = await res.json();
        setGlobalSettings(data || {});
      }
    } catch (err) {
      console.error('Failed to load global settings:', err);
    }
  };

  // Save global settings
  const handleSaveGlobalSettings = async (settings) => {
    try {
      const res = await fetch(`${API_URL}/admin/settings`, {
        method: 'PUT',
        headers: authHeaders,
        body: JSON.stringify(settings)
      });
      if (res.ok) {
        setGlobalSettings(settings);
        alert('Global settings saved!');
      }
    } catch (err) {
      console.error('Failed to save global settings:', err);
    }
  };

  // Update contact message status
  const handleUpdateContactStatus = async (contactId, status) => {
    try {
      const res = await fetch(`${API_URL}/admin/contact/${contactId}`, {
        method: 'PUT',
        headers: authHeaders,
        body: JSON.stringify({ status })
      });
      if (res.ok) {
        loadContactMessages();
        loadContactCount();
      }
    } catch (err) {
      console.error('Failed to update contact:', err);
    }
  };

  // Delete contact message
  const handleDeleteContact = async (messageId) => {
    if (!window.confirm('Delete this message?')) return;
    try {
      const res = await fetch(`${API_URL}/admin/contact/${messageId}`, {
        method: 'DELETE',
        headers: authHeaders
      });
      if (res.ok) {
        loadContactMessages();
        loadContactCount();
      }
    } catch (err) {
      console.error('Failed to delete contact:', err);
    }
  };

  // Load games from API
  const loadGames = async () => {
    try {
      const res = await fetch(`${API_URL}/admin/games`, { headers: authHeaders });
      const data = await res.json();
      setGames(data);
      
      // Load questions for all games
      const allQuestions = [];
      for (const game of data) {
        const qRes = await fetch(`${API_URL}/games/${game.game_id}/questions`);
        const qData = await qRes.json();
        allQuestions.push(...qData);
      }
      setQuestions(allQuestions);
    } catch (err) {
      console.error('Failed to load games:', err);
    }
  };

  // Load players from API
  const loadPlayers = async () => {
    try {
      const res = await fetch(`${API_URL}/admin/players`, { headers: authHeaders });
      const data = await res.json();
      setPlayers(data);
    } catch (err) {
      console.error('Failed to load players:', err);
    }
  };

  // Load local data (challenges from API, messages from localStorage) and pictures from API
  const loadLocalData = async () => {
    try {
      // Load pictures from API
      const picRes = await fetch(`${API_URL}/admin/pictures`, { headers: authHeaders });
      if (picRes.ok) {
        const picData = await picRes.json();
        setPictures(picData);
      }
      
      // Load challenges from API (snake_case to match database)
      const chalRes = await fetch(`${API_URL}/admin/challenges`, { headers: authHeaders });
      if (chalRes.ok) {
        const chalData = await chalRes.json();
        setChallenges(Array.isArray(chalData) ? chalData : []);
      }
      
      const savedMessages = localStorage.getItem('messages');
      if (savedMessages) setMessages(JSON.parse(savedMessages));
      
      const savedRewards = localStorage.getItem('rewards');
      if (savedRewards) setRewards(JSON.parse(savedRewards));
      
      const savedSettings = localStorage.getItem('pictureGridSettings');
      if (savedSettings) setPictureGridSettings(JSON.parse(savedSettings));
    } catch (err) {
      console.error('Failed to load local data:', err);
    }
  };

  // ============================================
  // GAME HANDLERS - API Connected
  // ============================================
  const handleCreateGame = async (gameData) => {
    try {
      const res = await fetch(`${API_URL}/games`, {
        method: 'POST',
        headers: authHeaders,
        body: JSON.stringify(gameData)
      });
      if (res.ok) {
        loadGames();
      }
    } catch (err) {
      console.error('Failed to create game:', err);
    }
  };

  const handleEditGame = (game) => {
    console.log('handleEditGame called with:', game);
    setEditingGame(game.game_id);
    setEditGameData({ ...game });
  };

  const handleSaveEditGame = async () => {
    if (!editGameData) return;
    try {
      const res = await fetch(`${API_URL}/games/${editGameData.game_id}`, {
        method: 'PUT',
        headers: authHeaders,
        body: JSON.stringify(editGameData)
      });
      if (res.ok) {
        setEditingGame(null);
        setEditGameData(null);
        loadGames();
      }
    } catch (err) {
      console.error('Failed to save game:', err);
    }
  };

  const handleCancelEditGame = () => {
    setEditingGame(null);
    setEditGameData(null);
  };

  const handleDeleteGame = async (gameId) => {
    // Confirmation already shown by CompleteGameAdminPanel
    try {
      const res = await fetch(`${API_URL}/games/${gameId}`, {
        method: 'DELETE',
        headers: authHeaders
      });
      if (res.ok) {
        setGames(prev => prev.filter(g => g.game_id !== gameId));
        console.log('✅ Game deleted:', gameId);
      } else {
        alert('Failed to delete game');
      }
    } catch (err) {
      console.error('Failed to delete game:', err);
      alert('Failed to delete game');
    }
  };

  const handleToggleGameStatus = async (game) => {
    try {
      await fetch(`${API_URL}/games/${game.game_id}`, {
        method: 'PUT',
        headers: authHeaders,
        body: JSON.stringify({ active: !game.active })
      });
      loadGames();
    } catch (err) {
      console.error('Failed to toggle game status:', err);
    }
  };

  const handleSetFeaturedGame = async (game) => {
    try {
      // Unfeature all games first
      for (const g of games) {
        if (g.featured) {
          await fetch(`${API_URL}/games/${g.game_id}`, {
            method: 'PUT',
            headers: authHeaders,
            body: JSON.stringify({ featured: false })
          });
        }
      }
      // Feature selected game
      await fetch(`${API_URL}/games/${game.game_id}`, {
        method: 'PUT',
        headers: authHeaders,
        body: JSON.stringify({ featured: true })
      });
      loadGames();
    } catch (err) {
      console.error('Failed to set featured game:', err);
    }
  };

  // ============================================
  // QUESTION HANDLERS - API Connected (FIXED!)
  // ============================================
  const handleAddQuestion = async (gameId, questionData) => {
    try {
      const res = await fetch(`${API_URL}/games/${gameId}/questions`, {
        method: 'POST',
        headers: authHeaders,
        body: JSON.stringify(questionData)
      });
      if (res.ok) {
        loadGames(); // Reload to get updated questions
      }
    } catch (err) {
      console.error('Failed to add question:', err);
    }
  };

  // 🔧 FIXED: Now calls API to persist edit
  const handleEditQuestion = async (questionId, questionData) => {
    try {
      const res = await fetch(`${API_URL}/questions/${questionId}`, {
        method: 'PUT',
        headers: authHeaders,
        body: JSON.stringify(questionData)
      });
      if (res.ok) {
        console.log('✅ Question updated in database');
        loadGames(); // Reload to get updated questions
      } else {
        const errorData = await res.json();
        console.error('❌ Failed to update question:', errorData);
        alert('Failed to save question: ' + (errorData.error || 'Unknown error'));
      }
    } catch (err) {
      console.error('❌ Failed to edit question:', err);
      alert('Failed to save question. Check console for details.');
    }
  };

  // 🔧 FIXED: Now calls API to persist delete
  const handleDeleteQuestion = async (question) => {
    if (!window.confirm(`Delete this question?\n\n"${question.question_text}"`)) return;
    
    try {
      const res = await fetch(`${API_URL}/questions/${question.question_id}`, {
        method: 'DELETE',
        headers: authHeaders
      });
      if (res.ok) {
        console.log('✅ Question deleted from database');
        loadGames(); // Reload to get updated questions
      } else {
        const errorData = await res.json();
        console.error('❌ Failed to delete question:', errorData);
        alert('Failed to delete question: ' + (errorData.error || 'Unknown error'));
      }
    } catch (err) {
      console.error('❌ Failed to delete question:', err);
      alert('Failed to delete question. Check console for details.');
    }
  };

  // 🔧 FIXED: Now calls API to persist toggle
  const handleToggleQuestionStatus = async (question) => {
    try {
      const res = await fetch(`${API_URL}/questions/${question.question_id}`, {
        method: 'PUT',
        headers: authHeaders,
        body: JSON.stringify({ active: !question.active })
      });
      if (res.ok) {
        console.log('✅ Question status toggled in database');
        loadGames(); // Reload to get updated questions
      } else {
        console.error('❌ Failed to toggle question status');
      }
    } catch (err) {
      console.error('❌ Failed to toggle question status:', err);
    }
  };

  // 🆕 NEW: Delete all questions for a game
  const handleDeleteAllQuestions = async (gameId) => {
    const game = games.find(g => g.game_id === gameId);
    const gameQuestions = questions.filter(q => q.game_id === gameId);
    
    if (gameQuestions.length === 0) {
      alert('No questions to delete for this quiz.');
      return;
    }
    
    if (!window.confirm(`⚠️ DELETE ALL ${gameQuestions.length} QUESTIONS from "${game?.name}"?\n\nThis cannot be undone!`)) return;
    
    // Double confirmation for safety
    if (!window.confirm(`Are you REALLY sure? This will permanently delete ${gameQuestions.length} questions.`)) return;
    
    try {
      const res = await fetch(`${API_URL}/games/${gameId}/questions`, {
        method: 'DELETE',
        headers: authHeaders
      });
      if (res.ok) {
        console.log('✅ All questions deleted from database');
        alert(`Successfully deleted ${gameQuestions.length} questions.`);
        loadGames(); // Reload to get updated questions
      } else {
        const errorData = await res.json();
        console.error('❌ Failed to delete all questions:', errorData);
        alert('Failed to delete questions: ' + (errorData.error || 'Unknown error'));
      }
    } catch (err) {
      console.error('❌ Failed to delete all questions:', err);
      alert('Failed to delete questions. Check console for details.');
    }
  };

  const handleBulkUpload = async (gameId, parsedQuestions) => {
    try {
      const res = await fetch(`${API_URL}/games/${gameId}/questions/bulk`, {
        method: 'POST',
        headers: authHeaders,
        body: JSON.stringify({ questions: parsedQuestions })
      });
      const data = await res.json();
      alert(data.message || `Uploaded ${parsedQuestions.length} questions`);
      loadGames();
    } catch (err) {
      console.error('Failed to bulk upload:', err);
    }
  };

  // ============================================
  // PICTURE HANDLERS - API Connected
  // ============================================
  const handleUploadPicture = async (gameId, pictureData) => {
    try {
      const res = await fetch(`${API_URL}/admin/pictures`, {
        method: 'POST',
        headers: authHeaders,
        body: JSON.stringify({ game_id: gameId, ...pictureData })
      });
      if (res.ok) {
        loadLocalData();
      }
    } catch (err) {
      console.error('Failed to upload picture:', err);
    }
  };

  const handleUpdatePicture = async (picture, updates) => {
    try {
      const res = await fetch(`${API_URL}/admin/pictures/${picture.picture_id}`, {
        method: 'PUT',
        headers: authHeaders,
        body: JSON.stringify(updates)
      });
      if (res.ok) {
        loadLocalData();
      }
    } catch (err) {
      console.error('Failed to update picture:', err);
    }
  };

  const handleDeletePicture = async (picture) => {
    if (!window.confirm('Delete this picture?')) return;
    try {
      const res = await fetch(`${API_URL}/admin/pictures/${picture.picture_id}`, {
        method: 'DELETE',
        headers: authHeaders
      });
      if (res.ok) {
        loadLocalData();
      }
    } catch (err) {
      console.error('Failed to delete picture:', err);
    }
  };

  const handleMovePicture = async (picture, direction) => {
    const gamePics = pictures.filter(p => p.game_id === picture.game_id)
      .sort((a, b) => a.display_order - b.display_order);
    const idx = gamePics.findIndex(p => p.picture_id === picture.picture_id);
    
    let swapWith = null;
    if (direction === 'up' && idx > 0) {
      swapWith = gamePics[idx - 1];
    } else if (direction === 'down' && idx < gamePics.length - 1) {
      swapWith = gamePics[idx + 1];
    }
    
    if (!swapWith) return;
    
    // Swap display_order values
    const newOrder1 = swapWith.display_order;
    const newOrder2 = picture.display_order;
    
    try {
      // Update both pictures in API
      await fetch(`${API_URL}/pictures/${picture.picture_id}`, {
        method: 'PUT',
        headers: authHeaders,
        body: JSON.stringify({ display_order: newOrder1 })
      });
      await fetch(`${API_URL}/pictures/${swapWith.picture_id}`, {
        method: 'PUT',
        headers: authHeaders,
        body: JSON.stringify({ display_order: newOrder2 })
      });
      
      // Update local state
      setPictures(prev => prev.map(p => {
        if (p.picture_id === picture.picture_id) return { ...p, display_order: newOrder1 };
        if (p.picture_id === swapWith.picture_id) return { ...p, display_order: newOrder2 };
        return p;
      }));
      
      console.log('✅ Pictures reordered');
    } catch (err) {
      console.error('Failed to reorder pictures:', err);
    }
  };

  // ============================================
  // CHALLENGE HANDLERS - Local State Only (API called by CompleteGameAdminPanel)
  // ============================================
  const handleApproveChallenge = (challenge, response) => {
    // Just update local state - API is called directly by CompleteGameAdminPanel
    const updated = challenges.map(c => 
      c.challenge_id === challenge.challenge_id 
        ? { ...c, status: 'approved', admin_response: response, tags_awarded: 50 } 
        : c
    );
    setChallenges(updated);
    console.log('✅ Challenge approved locally:', challenge.challenge_id);
  };

  const handleDeclineChallenge = (challenge, response) => {
    // Just update local state - API is called directly by CompleteGameAdminPanel
    const updated = challenges.map(c => 
      c.challenge_id === challenge.challenge_id 
        ? { ...c, status: 'declined', admin_response: response } 
        : c
    );
    setChallenges(updated);
    console.log('❌ Challenge declined locally:', challenge.challenge_id);
  };

  const handleSendMessage = (messageData) => {
    const newMessage = {
      id: Date.now(),
      ...messageData,
      timestamp: new Date().toISOString()
    };
    const updated = [newMessage, ...messages];
    setMessages(updated);
    localStorage.setItem('messages', JSON.stringify(updated));
  };

  const handleAwardReward = (player, amount, reason) => {
    const newReward = {
      id: Date.now(),
      playerId: player.player_id,
      amount,
      reason,
      timestamp: new Date().toISOString()
    };
    const updated = [...rewards, newReward];
    setRewards(updated);
    localStorage.setItem('rewards', JSON.stringify(updated));
  };

  const handleUpdatePictureGridSettings = (settings) => {
    setPictureGridSettings(settings);
    localStorage.setItem('pictureGridSettings', JSON.stringify(settings));
  };

  return (
    <div className="min-h-screen bg-gray-900">
      {/* Admin Header */}
      <div className="bg-gray-800 border-b border-gray-700 px-4 py-3 flex justify-between items-center">
        <div className="flex items-center gap-4">
          <h1 className="text-xl font-bold text-white">🔧 TRAK Admin</h1>
          <span className="text-gray-400 text-sm">
            {admin.name} ({admin.role})
          </span>
        </div>
        <button
          onClick={onLogout}
          className="bg-red-600 hover:bg-red-500 text-white px-4 py-2 rounded-lg text-sm"
        >
          Logout
        </button>
      </div>

      {/* Admin Tabs */}
      <div className="bg-gray-800 border-b border-gray-700 px-4">
        <div className="flex gap-1">
          <button
            onClick={() => setActiveAdminTab('games')}
            className={`px-4 py-3 text-sm font-medium rounded-t-lg ${
              activeAdminTab === 'games'
                ? 'bg-purple-600 text-white'
                : 'bg-gray-700 text-gray-300 hover:bg-gray-600'
            }`}
          >
            🎮 Game Management
          </button>
          <button
            onClick={() => setActiveAdminTab('adverts')}
            className={`px-4 py-3 text-sm font-medium rounded-t-lg ${
              activeAdminTab === 'adverts'
                ? 'bg-purple-600 text-white'
                : 'bg-gray-700 text-gray-300 hover:bg-gray-600'
            }`}
          >
            📺 Global Adverts
          </button>
          <button
            onClick={() => { setActiveAdminTab('contact'); loadContactMessages(); }}
            className={`px-4 py-3 text-sm font-medium rounded-t-lg flex items-center gap-2 ${
              activeAdminTab === 'contact'
                ? 'bg-purple-600 text-white'
                : 'bg-gray-700 text-gray-300 hover:bg-gray-600'
            }`}
          >
            📬 Contact Messages
            {contactCount > 0 && (
              <span className="bg-red-500 text-white text-xs px-2 py-0.5 rounded-full">
                {contactCount}
              </span>
            )}
          </button>
        </div>
      </div>

      {/* Tab Content */}
      <div className="p-4">
        {activeAdminTab === 'games' && (
          <CompleteGameAdminPanel
            games={games}
            pictures={pictures}
            questions={questions}
            challenges={challenges}
            messages={messages}
            rewards={rewards}
            messageTemplates={{}}
            players={players}
            onCreateGame={handleCreateGame}
            onEditGame={handleEditGame}
            onDeleteGame={handleDeleteGame}
            onToggleGameStatus={handleToggleGameStatus}
            onSetFeaturedGame={handleSetFeaturedGame}
            onSelectGame={() => {}}
            onUploadPicture={handleUploadPicture}
            onUpdatePicture={handleUpdatePicture}
            onDeletePicture={handleDeletePicture}
            onMovePicture={handleMovePicture}
            onAddQuestion={handleAddQuestion}
            onEditQuestion={handleEditQuestion}
            onDeleteQuestion={handleDeleteQuestion}
            onDeleteAllQuestions={handleDeleteAllQuestions}
            onToggleQuestionStatus={handleToggleQuestionStatus}
            onBulkUpload={handleBulkUpload}
            onApproveChallenge={handleApproveChallenge}
            onDeclineChallenge={handleDeclineChallenge}
            onSendMessage={handleSendMessage}
            onAwardReward={handleAwardReward}
            onUpdateTemplate={() => {}}
            editingGame={editingGame}
            editGameData={editGameData}
            setEditGameData={setEditGameData}
            onSaveEditGame={handleSaveEditGame}
            onCancelEditGame={handleCancelEditGame}
            pictureGridSettings={pictureGridSettings}
            onUpdatePictureGridSettings={handleUpdatePictureGridSettings}
            userRole={admin.role}
          />
        )}

        {activeAdminTab === 'adverts' && (
          <GlobalAdvertsPanel
            settings={globalSettings}
            onSave={handleSaveGlobalSettings}
          />
        )}

        {activeAdminTab === 'contact' && (
          <ContactMessagesPanel
            messages={contactMessages}
            onUpdateStatus={handleUpdateContactStatus}
            onDelete={handleDeleteContact}
          />
        )}
      </div>
    </div>
  );
};

// ============================================
// MAIN ADMIN APP
// ============================================
export default function AdminApp() {
  const [admin, setAdmin] = useState(null);
  const [token, setToken] = useState(null);

  // Check for existing session
  useEffect(() => {
    const savedToken = localStorage.getItem('admin_token');
    if (savedToken) {
      try {
        const payload = JSON.parse(atob(savedToken.split('.')[1]));
        if (payload.exp * 1000 > Date.now() && payload.is_admin) {
          setToken(savedToken);
          setAdmin({ 
            admin_id: payload.admin_id, 
            email: payload.email, 
            role: payload.role,
            name: payload.email.split('@')[0] 
          });
        } else {
          localStorage.removeItem('admin_token');
        }
      } catch {
        localStorage.removeItem('admin_token');
      }
    }
  }, []);

  const handleLogin = (adminData, tokenData) => {
    setAdmin(adminData);
    setToken(tokenData);
  };

  const handleLogout = () => {
    localStorage.removeItem('admin_token');
    setAdmin(null);
    setToken(null);
  };

  if (!admin || !token) {
    return <AdminLogin onLogin={handleLogin} />;
  }

  return <AdminWrapper admin={admin} token={token} onLogout={handleLogout} />;
}
